import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/config';
import {
  collection,
  onSnapshot,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
} from 'firebase/firestore';
import {
  LogOut,
  Trash2,
  RefreshCw,
  Users,
  AlertCircle,
  CheckCircle2,
  Clock,
  ChevronDown,
  X,
  Phone,
  Mail,
  Calendar,
  Wrench,
  MessageSquare,
  MapPin,
  User,
} from 'lucide-react';

const STATUS_LABELS = {
  new: { pt: 'Novo', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  contacted: { pt: 'Contactado', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  closed: { pt: 'Fechado', color: 'bg-green-100 text-green-700 border-green-200' },
};

function LeadModal({ lead, onClose, onUpdateStatus, onDelete }) {
  const [showStatus, setShowStatus] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (!lead) return null;

  const handleDelete = async () => {
    await onDelete(lead.id);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
        <div className="bg-[#C2410C] px-6 py-5 flex items-start justify-between">
          <div>
            <h2 className="text-white font-bold text-xl" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {lead.name}
            </h2>
            <p className="text-orange-200 text-sm mt-0.5">
              {lead.timestamp ? lead.timestamp.toLocaleDateString('pt-PT', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Data desconhecida'}
            </p>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white transition-colors mt-0.5">
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <a
              href={"mailto:" + lead.email}
              className="flex items-center gap-3 p-3 rounded-xl bg-zinc-50 border border-zinc-100 hover:border-[#C2410C] hover:bg-orange-50 transition-all group"
            >
              <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <Mail size={15} className="text-[#C2410C]" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wide">Email</p>
                <p className="text-sm text-zinc-700 font-medium truncate group-hover:text-[#C2410C] transition-colors">{lead.email || '—'}</p>
              </div>
            </a>

            <a
              href={"tel:" + lead.phone}
              className="flex items-center gap-3 p-3 rounded-xl bg-zinc-50 border border-zinc-100 hover:border-[#C2410C] hover:bg-orange-50 transition-all group"
            >
              <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <Phone size={15} className="text-[#C2410C]" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wide">Telefone</p>
                <p className="text-sm text-zinc-700 font-medium truncate group-hover:text-[#C2410C] transition-colors">{lead.phone || '—'}</p>
              </div>
            </a>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-3 p-3 rounded-xl bg-zinc-50 border border-zinc-100">
              <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <Wrench size={15} className="text-[#C2410C]" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wide">Serviço</p>
                <p className="text-sm text-zinc-700 font-medium truncate">{lead.service || '—'}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 rounded-xl bg-zinc-50 border border-zinc-100">
              <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <Calendar size={15} className="text-[#C2410C]" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wide">Data</p>
                <p className="text-sm text-zinc-700 font-medium">{lead.timestamp ? lead.timestamp.toLocaleDateString('pt-PT') : '—'}</p>
              </div>
            </div>
          </div>

          {lead.location && (
            <div className="flex items-center gap-3 p-3 rounded-xl bg-zinc-50 border border-zinc-100">
              <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <MapPin size={15} className="text-[#C2410C]" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wide">Localização</p>
                <p className="text-sm text-zinc-700 font-medium">{lead.location}</p>
              </div>
            </div>
          )}

          {lead.message && (
            <div className="p-4 rounded-xl bg-zinc-50 border border-zinc-100">
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare size={13} className="text-[#C2410C]" />
                <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wide">Mensagem</p>
              </div>
              <p className="text-sm text-zinc-700 leading-relaxed">{lead.message}</p>
            </div>
          )}

          <div className="flex items-center justify-between pt-1">
            <div className="relative">
              <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wide mb-1.5">Estado</p>
              <button
                onClick={() => setShowStatus(!showStatus)}
                className={"inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border " + (STATUS_LABELS[lead.status]?.color || 'bg-zinc-100 text-zinc-600 border-zinc-200')}
              >
                {STATUS_LABELS[lead.status]?.pt || lead.status}
                <ChevronDown size={12} />
              </button>
              {showStatus && (
                <div className="absolute left-0 top-14 z-10 bg-white border border-zinc-200 rounded-xl shadow-lg py-1 w-36">
                  {Object.entries(STATUS_LABELS).map(([key, val]) => (
                    <button
                      key={key}
                      onClick={() => { onUpdateStatus(lead.id, key); setShowStatus(false); }}
                      className="flex w-full px-4 py-2 text-xs text-zinc-700 hover:bg-zinc-50 font-medium transition-colors"
                    >
                      {val.pt}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <a
                href={"mailto:" + lead.email}
                className="px-4 py-2 bg-[#C2410C] text-white rounded-full text-xs font-semibold hover:bg-[#9a3009] transition-colors"
              >
                Responder
              </a>
              {!confirmDelete ? (
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="px-4 py-2 border border-zinc-200 text-zinc-500 rounded-full text-xs font-semibold hover:border-red-300 hover:text-red-600 hover:bg-red-50 transition-all"
                >
                  Eliminar
                </button>
              ) : (
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-full text-xs font-semibold hover:bg-red-700 transition-colors"
                >
                  Confirmar
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);

  useEffect(() => {
    const q = query(collection(db, 'leads'), orderBy('timestamp', 'desc'));
    const unsubscribe = onSnapshot(q, (snap) => {
      const updatedLeads = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        timestamp: d.data().timestamp?.toDate?.() || null,
      }));
      setLeads(updatedLeads);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const updateStatus = async (id, status) => {
    await updateDoc(doc(db, 'leads', id), { status });
  };

  const deleteLead = async (id) => {
    await deleteDoc(doc(db, 'leads', id));
    setDeleteConfirm(null);
  };

  const filtered = filter === 'all' ? leads : leads.filter((l) => l.status === filter);

  const counts = {
    all: leads.length,
    new: leads.filter((l) => l.status === 'new').length,
    contacted: leads.filter((l) => l.status === 'contacted').length,
    closed: leads.filter((l) => l.status === 'closed').length,
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      <header className="bg-white border-b border-zinc-200 px-6 py-4 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <span className="font-bold text-xl text-zinc-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }} data-testid="admin-logo">
            RJ <span className="text-[#C2410C]">Admin</span>
          </span>
          <span className="hidden sm:inline text-zinc-300">|</span>
          <span className="hidden sm:inline text-sm text-zinc-500">Gestão de Leads</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="hidden sm:block text-xs text-zinc-400 truncate max-w-[200px]">{user?.email}</span>
          <button
            onClick={logout}
            className="flex items-center gap-2 px-4 py-2 text-sm text-zinc-600 hover:text-zinc-900 border border-zinc-200 rounded-full hover:border-zinc-400 transition-all"
            data-testid="admin-logout-btn"
          >
            <LogOut size={15} />
            Sair
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total', value: counts.all, icon: Users, color: 'text-zinc-700' },
            { label: 'Novos', value: counts.new, icon: AlertCircle, color: 'text-blue-600' },
            { label: 'Contactados', value: counts.contacted, icon: Clock, color: 'text-amber-600' },
            { label: 'Fechados', value: counts.closed, icon: CheckCircle2, color: 'text-green-600' },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-xl border border-zinc-200 p-5 shadow-sm" data-testid={"admin-stat-" + i}>
              <s.icon size={18} className={s.color + " mb-3"} />
              <div className="text-2xl font-bold text-zinc-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                {s.value}
              </div>
              <div className="text-xs text-zinc-500 mt-0.5 font-medium">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {['all', 'new', 'contacted', 'closed'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={"px-4 py-2 rounded-full text-xs font-semibold transition-all " + (filter === f ? 'bg-[#C2410C] text-white' : 'bg-white border border-zinc-200 text-zinc-600 hover:border-zinc-400')}
              data-testid={"admin-filter-" + f}
            >
              {f === 'all' ? 'Todos' : STATUS_LABELS[f].pt} ({counts[f]})
            </button>
          ))}
        </div>

        <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <RefreshCw className="animate-spin text-zinc-400" size={24} />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-zinc-400">
              <Users size={40} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">Nenhum lead encontrado.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-leads-table">
                <thead>
                  <tr className="bg-zinc-50 border-b border-zinc-200">
                    <th className="text-left px-5 py-3.5 text-xs font-bold text-zinc-500 uppercase tracking-wide">Nome</th>
                    <th className="text-left px-5 py-3.5 text-xs font-bold text-zinc-500 uppercase tracking-wide hidden sm:table-cell">Email</th>
                    <th className="text-left px-5 py-3.5 text-xs font-bold text-zinc-500 uppercase tracking-wide hidden md:table-cell">Telefone</th>
                    <th className="text-left px-5 py-3.5 text-xs font-bold text-zinc-500 uppercase tracking-wide hidden lg:table-cell">Serviço</th>
                    <th className="text-left px-5 py-3.5 text-xs font-bold text-zinc-500 uppercase tracking-wide hidden xl:table-cell">Data</th>
                    <th className="text-left px-5 py-3.5 text-xs font-bold text-zinc-500 uppercase tracking-wide">Estado</th>
                    <th className="text-left px-5 py-3.5 text-xs font-bold text-zinc-500 uppercase tracking-wide">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {filtered.map((lead) => (
                    <LeadRow
                      key={lead.id}
                      lead={lead}
                      onUpdateStatus={updateStatus}
                      onDelete={setDeleteConfirm}
                      onOpen={() => setSelectedLead(lead)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {selectedLead && (
        <LeadModal
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onUpdateStatus={updateStatus}
          onDelete={deleteLead}
        />
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4" data-testid="delete-confirm-modal">
          <div className="bg-white rounded-2xl p-7 max-w-sm w-full shadow-2xl">
            <h3 className="font-bold text-lg text-zinc-900 mb-2">Eliminar lead?</h3>
            <p className="text-sm text-zinc-500 mb-6">Esta ação não pode ser desfeita.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 py-2.5 border border-zinc-200 rounded-full text-sm font-semibold text-zinc-600 hover:border-zinc-400 transition-colors"
                data-testid="delete-cancel-btn"
              >
                Cancelar
              </button>
              <button
                onClick={() => deleteLead(deleteConfirm)}
                className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-full text-sm font-semibold transition-colors"
                data-testid="delete-confirm-btn"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LeadRow({ lead, onUpdateStatus, onDelete, onOpen }) {
  const [showStatus, setShowStatus] = useState(false);

  return (
    <tr className="hover:bg-zinc-50 transition-colors" data-testid={"lead-row-" + lead.id}>
      <td className="px-5 py-4">
        <button onClick={onOpen} className="text-left">
          <div className="font-medium text-zinc-900 hover:text-[#C2410C] hover:underline underline-offset-2 transition-colors cursor-pointer">
            {lead.name}
          </div>
          {lead.message && (
            <div className="text-xs text-zinc-400 mt-0.5 max-w-[180px] truncate">{lead.message}</div>
          )}
        </button>
      </td>
      <td className="px-5 py-4 text-zinc-600 hidden sm:table-cell">
        <a href={"mailto:" + lead.email} className="hover:text-[#C2410C] transition-colors">
          {lead.email}
        </a>
      </td>
      <td className="px-5 py-4 text-zinc-600 hidden md:table-cell">{lead.phone || '—'}</td>
      <td className="px-5 py-4 text-zinc-600 hidden lg:table-cell max-w-[160px] truncate">{lead.service}</td>
      <td className="px-5 py-4 text-zinc-400 text-xs hidden xl:table-cell">
        {lead.timestamp ? lead.timestamp.toLocaleDateString('pt-PT') : '—'}
      </td>
      <td className="px-5 py-4">
        <div className="relative">
          <button
            onClick={() => setShowStatus(!showStatus)}
            className={"inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border " + (STATUS_LABELS[lead.status]?.color || 'bg-zinc-100 text-zinc-600 border-zinc-200')}
            data-testid={"lead-status-btn-" + lead.id}
          >
            {STATUS_LABELS[lead.status]?.pt || lead.status}
            <ChevronDown size={12} />
          </button>
          {showStatus && (
            <div className="absolute left-0 top-8 z-10 bg-white border border-zinc-200 rounded-xl shadow-lg py-1 w-36">
              {Object.entries(STATUS_LABELS).map(([key, val]) => (
                <button
                  key={key}
                  onClick={() => { onUpdateStatus(lead.id, key); setShowStatus(false); }}
                  className="flex w-full px-4 py-2 text-xs text-zinc-700 hover:bg-zinc-50 font-medium transition-colors"
                  data-testid={"lead-status-option-" + key}
                >
                  {val.pt}
                </button>
              ))}
            </div>
          )}
        </div>
      </td>
      <td className="px-5 py-4">
        <div className="flex items-center gap-1">
          <button
            onClick={onOpen}
            className="p-2 text-zinc-400 hover:text-[#C2410C] hover:bg-orange-50 rounded-lg transition-all"
            title="Ver detalhes"
            data-testid={"lead-open-btn-" + lead.id}
          >
            <User size={15} />
          </button>
          <button
            onClick={() => onDelete(lead.id)}
            className="p-2 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
            data-testid={"lead-delete-btn-" + lead.id}
          >
            <Trash2 size={15} />
          </button>
        </div>
      </td>
    </tr>
  );
}
